import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Flow } from 'signup-constants/Flow';
import { SignupEmbedder } from 'signup-embedder';
import { Locale } from 'signup-ui-lego-core/constants/Locale';
import { LoadingScreen } from '../app-active-loading/LoadingScreen';
import { getIsNewUserExperiment } from '../experiments/ExperimentUtils';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { useFeedbackWidget } from '../hooks/useFeedback';
import { isEnglishLanguage, isBrowserAndApplicationLanguageForLocale } from '../i18n/LanguageUtils';
import { getFlowKey } from '../routing/getFlowKey';
import { getActiveLoading } from '../store/loading-status/selectors';
import { useTracker } from '../tracking/useTracker';
import { Step } from '../views/Step';
import { getIsMobile } from './deviceUtils';
import { isFlow } from './isFlow';
export const useChatBot = () => {
  const {
    currentStep
  } = useCurrentStep();
  const [loaded, setLoaded] = useState(false);
  const [handlerAdded, setHandlerAdded] = useState(false);
  const isNewUser = useSelector(getIsNewUserExperiment);
  const flowKey = getFlowKey();
  const Tracker = useTracker();
  const activeLoading = useSelector(getActiveLoading);
  const isLoading = activeLoading && LoadingScreen[activeLoading];
  const shouldShowFeedbackWidget = useFeedbackWidget();
  const isFrenchLanguage = isBrowserAndApplicationLanguageForLocale(Locale.fr);
  const isGermanLanguage = isBrowserAndApplicationLanguageForLocale(Locale.de);
  const isJapaneseLanguage = isBrowserAndApplicationLanguageForLocale(Locale.ja);
  const isChatbotEnabledByLanguage = isEnglishLanguage() || isFlow(Flow.Crm) && (isFrenchLanguage || isGermanLanguage || isJapaneseLanguage);
  useEffect(() => {
    const windowObject = window;

    const showBot = () => {
      const allowedFlows = [Flow.Crm, Flow.Marketing, Flow.Sales, Flow.Service, Flow.CmsFree, Flow.HubSpotPartners, Flow.HubspotForStartups, Flow.AssetProvider, Flow.ClassroomTraining, Flow.Developers, Flow.CmsDevelopers, Flow.DirectoryListing, Flow.SolutionsProvider, Flow.Trial];
      const feedbackSteps = [Step.CrmExperienceLevel, Step.HubInterest];

      if (feedbackSteps.includes(currentStep) && shouldShowFeedbackWidget) {
        return false;
      }

      return !getIsMobile() && allowedFlows.includes(flowKey) && isChatbotEnabledByLanguage && !SignupEmbedder.isActive();
    };

    const setupChat = () => {
      if (showBot() && windowObject.HubSpotConversations.widget && !loaded) {
        //This is so that we can use the Manage Cookies modal while the chatbot is on the page
        const interval = setInterval(() => {
          const container = document.getElementById('hubspot-messages-iframe-container');

          if (container) {
            container.style.zIndex = '1';
            clearInterval(interval);
          }
        }, 500);
        windowObject.HubSpotConversations.widget.load();
        setLoaded(true); // it is actually not loaded but more like it's loading.
      }
    };

    if (windowObject.HubSpotConversations) {
      setupChat();

      if (!handlerAdded) {
        setHandlerAdded(true);
        windowObject.HubSpotConversations.on('conversationStarted', ({
          conversation
        }) => {
          Tracker.signupInteraction('chatbot-conversation-started', {
            currentStep,
            conversationId: conversation.conversationId
          });
        });
        windowObject.HubSpotConversations.on('conversationClosed', ({
          conversation
        }) => {
          Tracker.signupInteraction('chatbot-conversation-ended', {
            currentStep,
            conversationId: conversation.conversationId
          });
        });
      }
    } else {
      windowObject.hsConversationsOnReady = [() => {
        setupChat();
      }];
    }

    return () => {
      if (!showBot() && loaded) {
        windowObject.HubSpotConversations.widget.remove();
        setLoaded(false);
      }
    };
  }, [currentStep, loaded, isNewUser, flowKey, Tracker, isLoading, handlerAdded, setHandlerAdded, isChatbotEnabledByLanguage, shouldShowFeedbackWidget]);
};